import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { enrollmentAPI, studentAPI, batchAPI } from '../services/api';
import { formatApiError } from '../utils/errorUtils';
import Modal from '../components/Modal';
import ResponsiveDataList from '../components/ResponsiveDataList';
import './EnrollmentsPage.css';

function EnrollmentsPage({
  studentId = null,
  readOnly = false,
  title = 'Enrollment Management',
  subtitle = ''
}) {
  const [enrollments, setEnrollments] = useState([]);
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    StudentID: '',
    BatchID: '',
    Status: 'Active',
  });

  const fetchEnrollments = useCallback(async () => {
    try {
      setLoading(true);
      const response = studentId ? await enrollmentAPI.getByStudent(studentId) : await enrollmentAPI.getAll();
      setEnrollments(response.data || []);
      setError('');
    } catch (err) {
      setError('Failed to fetch enrollments: ' + formatApiError(err, 'Unable to load enrollments'));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    const loadData = async () => {
      await fetchEnrollments();
    };

    loadData();
    if (!readOnly) {
      fetchStudents();
    }
    fetchBatches();
  }, [fetchEnrollments, readOnly]);

  const fetchStudents = async () => {
    try {
      const response = await studentAPI.getAll();
      setStudents(response.data || []);
    } catch (err) {
      console.error('Failed to fetch students:', err);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await batchAPI.getAll();
      setBatches(response.data || []);
    } catch (err) {
      console.error('Failed to fetch batches:', err);
    }
  };

  const getStudentName = (id) => {
    const student = students.find((s) => String(s.StudentID) === String(id));
    return student ? `${student.FirstName} ${student.LastName}` : 'N/A';
  };

  const getBatchName = (id) => {
    const batch = batches.find((b) => String(b.BatchID) === String(id));
    return batch ? batch.BatchName : 'N/A';
  };

  const filteredEnrollments = useMemo(() => {
    return (enrollments || []).filter((enrollment) => {
      if (readOnly) {
        return getBatchName(enrollment.BatchID).toLowerCase().includes(searchTerm.toLowerCase());
      }
      const studentName = getStudentName(enrollment.StudentID);
      return (
        studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getBatchName(enrollment.BatchID).toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [enrollments, readOnly, searchTerm, students, batches]);

  const handleOpenModal = (enrollment = null) => {
    if (enrollment) {
      setEditingId(enrollment.EnrollmentID);
      setFormData(enrollment);
    } else {
      setEditingId(null);
      setFormData({
        StudentID: '',
        BatchID: '',
        Status: 'Active',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await enrollmentAPI.update(editingId, formData);
      } else {
        await enrollmentAPI.create(formData);
      }
      await fetchEnrollments();
      handleCloseModal();
      setError('');
    } catch (err) {
      setError('Failed to save enrollment: ' + formatApiError(err, 'Unable to save enrollment'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this enrollment?')) {
      try {
        await enrollmentAPI.delete(id);
        fetchEnrollments();
        setError('');
      } catch (err) {
        setError('Failed to delete enrollment: ' + formatApiError(err, 'Unable to delete enrollment'));
      }
    }
  };

  // Define columns for ResponsiveDataList
  const columns = useMemo(() => {
    const cols = [];
    if (!readOnly) {
      cols.push({
        header: 'Student',
        accessor: (row) => getStudentName(row.StudentID)
      });
    }
    cols.push({
      header: 'Batch Name',
      accessor: (row) => getBatchName(row.BatchID)
    });
    cols.push({
      header: 'Status',
      accessor: 'Status',
      render: (row) => (
        <span className={`status-badge ${String(row.Status).toLowerCase()}`}>
          {row.Status}
        </span>
      )
    });
    if (!readOnly) {
      cols.push({
        header: 'Actions',
        accessor: 'EnrollmentID',
        render: (row) => (
          <div className="action-buttons flex gap-2">
            <button
              type="button"
              className="btn btn-small btn-primary"
              onClick={() => handleOpenModal(row)}
            >
              <FaEdit /> Edit
            </button>
            <button
              type="button"
              className="btn btn-small btn-danger"
              onClick={() => handleDelete(row.EnrollmentID)}
            >
              <FaTrash /> Delete
            </button>
          </div>
        )
      });
    }
    return cols;
  }, [readOnly, students, batches]);

  return (
    <div className="enrollments-page">
      {error && <div className="alert alert-error">{error}</div>}

      <ResponsiveDataList
        title={title}
        subtitle={subtitle}
        data={filteredEnrollments}
        columns={columns}
        onAddNew={!readOnly ? () => handleOpenModal() : null}
        addNewLabel="+ New Enrollment"
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={readOnly ? 'Search by batch name...' : 'Search student or batch...'}
        loading={loading}
        emptyStateTitle="No enrollments found"
        emptyStateSubtitle="There are currently no matching batch enrollments recorded."
        primaryKey="EnrollmentID"
      />

      {!readOnly && (
        <Modal
          isOpen={isModalOpen}
          title={editingId ? 'Edit Enrollment' : 'New Enrollment'}
          onClose={handleCloseModal}
        >
          <form onSubmit={handleSubmit} className="enrollment-form">
            <div className="form-group">
              <label>Student *</label>
              <select
                name="StudentID"
                value={formData.StudentID}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Student</option>
                {students.map((student) => (
                  <option key={student.StudentID} value={student.StudentID}>
                    {student.FirstName} {student.LastName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Batch *</label>
              <select
                name="BatchID"
                value={formData.BatchID}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Batch</option>
                {batches.map((batch) => (
                  <option key={batch.BatchID} value={batch.BatchID}>
                    {batch.BatchName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                name="Status"
                value={formData.Status}
                onChange={handleInputChange}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update Enrollment' : 'Add Enrollment'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseModal}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default EnrollmentsPage;
